/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import promo324 from "../../../assets/images/promo-324.svg";
import promo600 from "../../../assets/images/promo-600.svg";
import promo800 from "../../../assets/images/promo-800.svg";
import promo1024 from "../../../assets/images/promo-1024.svg";
import promo1280 from "../../../assets/images/promo-1280.svg";
import { Breadcrumbs } from "../../../components/Breadcrumbs";
import { PageContainer } from "../../../components/layout/PageContainer";
import TitleBox from "../../../components/TitleBox";
import { selectRemoteConfig } from "../../../services/firebase/remote-config/remoteConfig.slice";
import { TestIds } from "../../../testIds";
import { CONTENT_LAYOUT_WIDTH } from "../../../util/consts";
import { useScrollRestoration } from "../../../util/useScrollRestoration";
import { selectContactInfo, selectCurrentFarmerAccount } from "../../azure-b2c/storage";
import useQuoteProvider from "../../quotes/components/QuoteModal/useQuoteModal";
import TypesenseSearch from "../../typesense/Search";
import ProductFilter, { ComponentFilters } from "../components/ProductFilter";
import PromoBanner from "../components/PromoBanner/PromoBanner";
import { PRODUCTS_ROUTES, PRODUCTS_TRADER_DEALS_ROUTE } from "../constants";
import { useProductFilters } from "../hooks/useProductFilters";

const traderDealsBannerSources = [
  { src: promo324 },
  { src: promo600, minWidth: 480 },
  { src: promo800, minWidth: 680 },
  { src: promo1024, minWidth: 900 },
  { src: promo1280, minWidth: 1200 },
];

const TraderDealsFilters = [
  ComponentFilters.SEARCH_TYPESENSE,
  ComponentFilters.CATEGORY,
  ComponentFilters.PRICE,
  ComponentFilters.IN_STOCK,
];

const breadcrumbItems = [{ text: "Shop", href: PRODUCTS_ROUTES.BASE }, { text: "Trader Deals" }];

export const TraderDeals = () => {
  const selectedAccount = useSelector(selectCurrentFarmerAccount);
  const relationshipOwner = useSelector(selectContactInfo);
  const { traderDealsFilterValue } = useSelector(selectRemoteConfig);

  const [categories, setCategories] = useState<string[]>([]);

  const { selectedFilters, sortBy, setSortBy, currentPage, clearFilters, getIsFilterApplied } =
    useProductFilters();

  const isFilterApplied = getIsFilterApplied(TraderDealsFilters);

  const traderSelectedFilters = useMemo(
    () => ({
      ...selectedFilters,
      promotions: { value: traderDealsFilterValue, setValue: () => undefined },
    }),
    [selectedFilters, traderDealsFilterValue]
  );

  const { pathname } = useLocation();

  const ref = useRef<HTMLElement>(null);
  useScrollRestoration({
    ref,
    scrollYPositionKey: sessionStorage.scrollYPositionOnProducts,
    path: pathname,
  });

  useEffect(() => {
    document
      .getElementById(TestIds.pages.mainScrollContainer)
      ?.scrollTo({ top: 0, behavior: "auto" });
  }, [currentPage, selectedAccount]);

  return (
    <PageContainer
      data-testid={TestIds.feat.product.traderDeals.page}
      sx={{
        maxWidth: `${CONTENT_LAYOUT_WIDTH}px`,
        gap: "1.5rem",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Breadcrumbs items={breadcrumbItems} />
      <TitleBox display="Trader Deals" />
      <PromoBanner
        sources={traderDealsBannerSources}
        alt="Rural never misses a good deal"
        testId={TestIds.feat.product.traderDeals.banner}
      />
      <ProductFilter
        components={TraderDealsFilters}
        filters={{ categories }}
        selectedFilters={traderSelectedFilters}
        items={[]}
      />
      <TypesenseSearch
        accountNumber={selectedAccount?.accountNumber}
        setCategoriesCallback={setCategories}
        data={[]}
        handleClearFilters={clearFilters}
        isFilterApplied={isFilterApplied}
        sortBy={sortBy}
        setSortBy={setSortBy}
        selectedFilters={traderSelectedFilters}
        currentPage={currentPage}
        relationshipOwner={relationshipOwner}
        path={PRODUCTS_TRADER_DEALS_ROUTE}
      />
    </PageContainer>
  );
};

export default () => useQuoteProvider(TraderDeals);
